/**
 * "Mi licencia" page (Sprint 227 — Stream C, REQ-BIL-018 / ADR-097).
 *
 * Read-only panel for the ACTIVE tenant: plan, applied limits and real usage.
 * Renders ONLY with a 200 from `GET /api/v1/licenses/me`; on 403
 * (LICENSE_VIEW_DENIED) or any error the panel shows a denied state and the
 * sidebar entry is hidden (fail-closed, SEC-003).
 */

import { AlertTriangle, BadgeCheck, CircleDollarSign, Database, Layers, Lock, ShieldAlert } from 'lucide-react';
import { useI18n } from '@/i18n';
import { useMyLicense } from '../hooks/useMyLicense';
import type { PlanLimits } from '../types';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Best-effort extraction of known limit keys from tenant.settings. */
function extractLimits(limits: Record<string, unknown>): PlanLimits {
  const storageConfig = (limits.storage_config ?? {}) as Record<string, unknown>;
  const num = (value: unknown): number | null =>
    typeof value === 'number' ? value : null;
  return {
    maxAgents: num(limits.max_agents),
    maxCollections: num(limits.max_collections),
    maxMembers: num(limits.max_members),
    maxTokensMonth: num(limits.max_tokens_month),
    maxStorageGb: num(storageConfig.max_storage_gb),
  };
}

export function MyLicensePage() {
  const { t } = useI18n();
  const { data, isLoading, isError } = useMyLicense();

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="h-8 w-64 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="mt-6 h-40 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <ShieldAlert className="h-12 w-12 text-muted-foreground" />
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('license.denied.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('license.denied.message')}
          </p>
        </div>
      </div>
    );
  }

  const limits = extractLimits(data.limits);
  // ADR-149 D1: cap del pool del centro (o cap directo) como " / N".
  const webCap = (
    budgets: { component: string; limit: number }[] | undefined,
    component: string,
  ): string | undefined => {
    const entry = (budgets ?? []).find((budget) => budget.component === component);
    return entry ? ` / ${entry.limit}` : undefined;
  };
  const storeLimits: Array<{ label: string; value: string; limit?: string }> = [
    {
      label: t('license.usage.agents'),
      value: String(data.usage.agents),
      limit: limits.maxAgents != null ? ` / ${limits.maxAgents}` : undefined,
    },
    {
      label: t('license.usage.collections'),
      value: String(data.usage.collections),
      limit: limits.maxCollections != null ? ` / ${limits.maxCollections}` : undefined,
    },
    {
      label: t('license.usage.documents'),
      value: String(data.usage.documents),
    },
    {
      label: t('license.usage.members'),
      value: String(data.usage.members),
      limit: limits.maxMembers != null ? ` / ${limits.maxMembers}` : undefined,
    },
    {
      label: t('license.usage.storage'),
      value: formatBytes(data.usage.storage_bytes),
      limit:
        limits.maxStorageGb != null ? ` / ${limits.maxStorageGb} GB` : undefined,
    },
    {
      label: t('license.usage.tokensMonth'),
      value:
        data.usage.tokens_month != null
          ? data.usage.tokens_month.toLocaleString()
          : '—',
      limit:
        limits.maxTokensMonth != null
          ? ` / ${limits.maxTokensMonth.toLocaleString()}`
          : undefined,
    },
    {
      // ADR-149: consumo web por tramos (el cap es el pool del centro, no el plan).
      label: t('license.usage.webQueries'),
      value: String(data.usage.web_queries ?? 0),
      limit: webCap(data.web_budget, 'web_queries'),
    },
    {
      label: t('license.usage.webFetches'),
      value: String(data.usage.web_fetches ?? 0),
      limit: webCap(data.web_budget, 'web_fetches'),
    },
    {
      label: t('license.usage.webActiveUsers'),
      value: String(data.usage.web_active_users ?? 0),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
          <CircleDollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('license.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{data.tenant_name}</p>
        </div>
      </div>

      {/* Plan */}
      <section className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-card p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <BadgeCheck className="h-4 w-4" /> {t('license.plan.title')}
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-blue-50 dark:bg-blue-900/30 px-3 py-1 text-sm font-medium text-blue-700 dark:text-blue-300">
            {data.plan_eff ?? data.plan_id ?? t('license.plan.notSet')}
          </span>
          {data.features.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {data.features.map((feature) => (
                <span
                  key={feature}
                  className="rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300"
                >
                  {feature}
                </span>
              ))}
            </span>
          )}
        </div>
      </section>

      {/* Limits */}
      <section className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-card p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <Lock className="h-4 w-4" /> {t('license.limits.title')}
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <LimitCard label={t('license.limits.maxAgents')} value={limits.maxAgents} />
          <LimitCard label={t('license.limits.maxCollections')} value={limits.maxCollections} />
          <LimitCard label={t('license.limits.maxMembers')} value={limits.maxMembers} />
          <LimitCard label={t('license.limits.maxStorage')} value={limits.maxStorageGb != null ? `${limits.maxStorageGb} GB` : null} />
          <LimitCard label={t('license.limits.maxTokensMonth')} value={limits.maxTokensMonth != null ? limits.maxTokensMonth.toLocaleString() : null} />
        </div>
      </section>

      {/* Usage */}
      <section className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-card p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          <Database className="h-4 w-4" /> {t('license.usage.title')}
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {storeLimits.map((item) => (
            <div
              key={item.label}
              className="rounded-md border border-gray-100 dark:border-gray-800 p-3"
            >
              <div className="text-xs text-muted-foreground">{item.label}</div>
              <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {item.value}
                {item.limit && (
                  <span className="text-sm font-normal text-muted-foreground">
                    {item.limit}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        {data.feature_configs.length > 0 && (
          <details className="mt-4">
            <summary className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Layers className="h-4 w-4" /> {t('license.featureConfigs')}
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded bg-gray-50 dark:bg-gray-900 p-3 text-xs">
              {JSON.stringify(data.feature_configs, null, 2)}
            </pre>
          </details>
        )}
      </section>

      <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5" />
        <span>{t('license.readOnlyHint')}</span>
      </div>
    </div>
  );
}

function LimitCard({ label, value }: { label: string; value: number | string | null }) {
  return (
    <div className="rounded-md border border-gray-100 dark:border-gray-800 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
        {value != null ? value : '—'}
      </div>
    </div>
  );
}